import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Trash2, Download, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeading, Card, inputCls } from "@/components/admin/ui";

const TYPE_LABELS = { pickup_help: "Pickup help", kabadiwala: "Kabadiwala", city_request: "City request", business: "Business", feedback: "Feedback", other: "Other" };
const STATUS = [["new", "New", "bg-[var(--marigold-soft)] text-[#7A4B07]"], ["in_progress", "In progress", "bg-[#E7EEF9] text-[#264B86]"], ["resolved", "Resolved", "bg-[var(--leaf-soft)] text-[var(--leaf-dark)]"]];

function EnquiryCard({ e, i, reload }) {
  const [note, setNote] = useState("");
  const update = async (status) => {
    try { await api.patch(`/admin/enquiries/${e.id}`, { status, note: note || undefined }); setNote(""); reload(); toast.success("Updated"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/admin/enquiries/${e.id}`); reload(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Card className="p-5" data-testid={`enquiry-card-${i}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-mono font-semibold">{e.reference}</span>
        <span className="px-2 py-0.5 rounded-full bg-[var(--paper-2)] font-semibold">{TYPE_LABELS[e.type]}</span>
        {STATUS.filter(([k]) => k === e.status).map(([k, l, c]) => <span key={k} className={`px-2 py-0.5 rounded-full font-semibold ${c}`} data-testid={`enquiry-status-${i}`}>{l}</span>)}
        {e.alert_status && <span className="text-[var(--ink-soft)]" data-testid={`enquiry-alert-${i}`}>· email alert {e.alert_status}</span>}
        <span className="text-[var(--ink-soft)] ml-auto">{new Date(e.created_at).toLocaleString("en-IN")}</span>
      </div>
      <div className="mt-3 font-bold">{e.name}</div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--ink-soft)]">
        {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--leaf-dark)]"><Phone size={13} />{e.phone}</a>}
        {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-[var(--leaf-dark)]"><Mail size={13} />{e.email}</a>}
        {e.phone && <a href={`https://wa.me/${e.phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}?text=${encodeURIComponent(`Namaste ${e.name}, this is Kindsaver regarding your enquiry ${e.reference}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#128C4B] font-semibold" data-testid={`enquiry-whatsapp-${i}`}><MessageCircle size={13} />WhatsApp reply</a>}
        {(e.city || e.locality) && <span className="inline-flex items-center gap-1"><MapPin size={13} />{[e.locality, e.city].filter(Boolean).join(", ")}</span>}
      </div>
      {e.message && <p className="mt-3 text-sm whitespace-pre-wrap bg-[var(--paper)] rounded-xl p-3">{e.message}</p>}
      {e.notes?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-[var(--ink-soft)]">{e.notes.map((n, k) => <li key={k}><b>{n.by}:</b> {n.text}</li>)}</ul>
      )}
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Add an internal note (optional)" className={`${inputCls} flex-1`} data-testid={`enquiry-note-${i}`} />
        <div className="flex gap-2">
          {STATUS.filter(([k]) => k !== e.status).map(([k, l]) => (
            <button key={k} onClick={() => update(k)} className="px-3 py-2 rounded-xl border text-xs font-semibold hover:border-[var(--leaf)] whitespace-nowrap" data-testid={`enquiry-set-${k}-${i}`}>Mark {l.toLowerCase()}</button>
          ))}
          <button onClick={remove} className="p-2 rounded-xl text-red-600 hover:bg-red-50" aria-label="Delete" data-testid={`enquiry-delete-${i}`}><Trash2 size={15} /></button>
        </div>
      </div>
    </Card>
  );
}

export default function EnquiriesInbox() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const load = () => api.get("/admin/enquiries", { params: { status: status || undefined, type: type || undefined } }).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [status, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    const cols = ["reference", "created_at", "type", "status", "name", "phone", "email", "city", "locality", "message"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...(rows || []).map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `kindsaver-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div data-testid="admin-enquiries">
      <AdminHeading title="Enquiries" subtitle="Messages from the contact form.">
        <div className="flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="enquiry-filter-status">
            <option value="">All statuses</option>{STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="enquiry-filter-type">
            <option value="">All types</option>{Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-ghost !py-2 !px-3 text-sm" aria-label="Export CSV" data-testid="enquiry-export-btn"><Download size={15} /></button>
        </div>
      </AdminHeading>
      <div className="space-y-3">
        {(rows || []).map((e, i) => <EnquiryCard key={e.id} e={e} i={i} reload={load} />)}
        {rows && rows.length === 0 && <Card className="p-10 text-center text-[var(--ink-soft)]" data-testid="enquiries-empty">No enquiries yet.</Card>}
      </div>
    </div>
  );
}
